export type WorkspacePlatformId = "claude" | "chatgpt";

export interface WorkspaceSetupLink {
  label: string;
  href: string;
  download?: boolean;
}

export interface WorkspaceSetupPath {
  platform: WorkspacePlatformId;
  label: string;
  actions: string[];
  screenshotIds: string[];
  links: WorkspaceSetupLink[];
  note?: string;
}

export interface WorkspaceSetupStep {
  id: string;
  kicker: string;
  title: string;
  instruction: string;
  screenshotIds?: string[];
  paths: WorkspaceSetupPath[];
  expected: string;
  verify: string[];
  recover: string[];
  stop: string;
  prompt?: {
    label: string;
    value: string;
  };
}

export const CHIEF_OF_STAFF_SETUP_PROMPT = `You are my bounded Chief of Staff for this setup test.

Prepare one ranked priority brief for [REVIEWER] using only [ONE APPROVED SOURCE]. Stay read-only.

For each priority:
- Explain why it matters now.
- Link to the evidence.
- Name the owner only when the source states one.
- Recommend the smallest useful next step.

Separate confirmed facts from questions. Do not send or draft messages, change records, download files, publish, purchase, or create an automation.

Stop before using another source or tool, when evidence is missing or conflicting, or when an action could affect another person. End by asking me to review the brief.`;

export const LONGHAND_WORKSPACE_SETUP_STEPS: WorkspaceSetupStep[] = [
  {
    id: "setup-desktop-app",
    kicker: "Choose one home base",
    title: "Download the desktop app and sign in.",
    instruction: "Pick Claude or ChatGPT for this setup. Keep the account, files, connectors, and test run in one approved workspace.",
    paths: [
      {
        platform: "claude",
        label: "Claude path",
        actions: [
          "Open the official Claude download page and choose the installer for Mac or Windows.",
          "Run the installer, open Claude Desktop, and sign in with the account approved for this work.",
          "Start a new chat, type a synthetic test message, close it, then reopen it from history.",
        ],
        screenshotIds: ["agent-workspace"],
        links: [
          { label: "Download Claude Desktop", href: "https://claude.ai/download" },
          { label: "See the Claude setup walkthrough", href: "/claude-fundamentals#step-download-desktop" },
        ],
      },
      {
        platform: "chatgpt",
        label: "ChatGPT path",
        actions: [
          "Open the official ChatGPT desktop page and choose the installer for your computer.",
          "Run the installer, open ChatGPT, and sign in with the account approved for this work.",
          "Start a new chat, type a synthetic test message, close it, then reopen it from history.",
        ],
        screenshotIds: ["chat-home"],
        links: [
          { label: "Download ChatGPT Desktop", href: "https://openai.com/chatgpt/desktop/" },
          { label: "See the paired desktop setup", href: "/ai-agent-starter#step-desktop-app" },
        ],
      },
    ],
    expected: "The approved desktop app is open, you are signed into the intended account, and a synthetic test chat appears in history.",
    verify: [
      "The account name and workspace match the one your organization approved.",
      "You can start, close, and reopen a chat.",
      "You know where the app stores conversation history and settings.",
    ],
    recover: [
      "The installer will not open: confirm your operating system and ask IT whether app installation is managed.",
      "The expected tools are missing: check the account, plan, and workspace before continuing. Availability can differ by plan and administrator policy.",
    ],
    stop: "Do not sign into a personal account with work data or bypass an organization-managed installation rule.",
  },
  {
    id: "setup-browser-access",
    kicker: "Keep the live browser supervised",
    title: "Install the browser extension or enable approved browser access.",
    instruction: "Use browser access only when the work lives on a live page. Start on a public, low-stakes page and keep site permissions narrow.",
    paths: [
      {
        platform: "claude",
        label: "Claude path",
        actions: [
          "Confirm you are using Chrome on a computer and that Claude for Chrome is available on your plan and workspace.",
          "Open the verified Chrome Web Store listing, confirm Anthropic is the publisher, choose Add to Chrome, and pin the extension.",
          "Open the extension, sign in with the same Claude account, and open a public help or documentation page.",
          "Ask Claude to summarize the page. Allow access once or only for that site, then review the approved-sites list in extension settings.",
        ],
        screenshotIds: ["install-extension", "site-access"],
        links: [
          { label: "Install Claude for Chrome", href: "https://chromewebstore.google.com/detail/claude/fcoeoabgfenejglbffodgkkbkcdhcgfn" },
          { label: "Follow the complete browser walkthrough", href: "/claude-masterclass#module-2" },
        ],
        note: "The repository's Claude walkthrough describes this as a paid-plan beta in Chrome. Confirm the live listing and your current plan before relying on it.",
      },
      {
        platform: "chatgpt",
        label: "ChatGPT path",
        actions: [
          "Do not install an extension that merely uses the ChatGPT name. This guide does not have a verified first-party ChatGPT extension flow.",
          "Open ChatGPT and find its current Web, Search, Browser, or Cloud browser control if your approved account provides one.",
          "Open the permission settings, choose Always ask or the narrowest available site rule, and add only a public test site if the interface requires it.",
          "Ask for a summary of that public page, compare it with the page yourself, and remove the site's access after the test if it is no longer needed.",
        ],
        screenshotIds: ["cloud-browser"],
        links: [
          { label: "See the browser safety walkthrough", href: "/ai-30/program/prep#setup-browser-access" },
        ],
        note: "ChatGPT browser labels and availability vary by account. Use the built-in capability your organization has approved rather than a look-alike browser extension.",
      },
    ],
    expected: "The approved browser capability can read one public test page, and its site permission is visible and removable.",
    verify: [
      "The extension publisher or built-in browser capability is the approved one.",
      "Access is limited to one action or one site, not every website by default.",
      "The result matches the visible page and no page content redirected the task.",
    ],
    recover: [
      "No browser capability appears: continue with ordinary chat and paste only approved text. Browser access is optional for the rest of the setup.",
      "The result includes content not on the page: revoke site access, start a clean chat, and retry with a narrower request on a trusted public page.",
    ],
    stop: "Do not test on banking, investing, health, legal, payroll, administrator, or other sensitive accounts. Decline any unexpected action or permission request.",
  },
  {
    id: "setup-connectors",
    kicker: "Connect one source of truth",
    title: "Add one approved connector and prove it works.",
    instruction: "Connect the lowest-risk source that can answer a real question, then verify the answer against the source before adding anything else.",
    paths: [
      {
        platform: "claude",
        label: "Claude path",
        actions: [
          "In Claude Desktop, open Settings and find Connectors, Connections, or MCP.",
          "Choose one approved, low-risk source such as Calendar or Drive and read every requested permission.",
          "Complete the source's sign-in, confirm the intended account and workspace, then return to Claude.",
          "Ask one read-only question only that source can answer and require a direct link to the supporting record.",
        ],
        screenshotIds: ["connections"],
        links: [
          { label: "See the Claude connector walkthrough", href: "/claude-fundamentals#step-set-up-connectors" },
          { label: "See connections in the Claude Masterclass", href: "/claude-masterclass#module-3" },
        ],
      },
      {
        platform: "chatgpt",
        label: "ChatGPT path",
        actions: [
          "In ChatGPT, open Settings and find Apps, Connectors, or Connected apps.",
          "Choose one approved, low-risk source and inspect its permission or confirmation controls before connecting.",
          "Complete the source's sign-in, confirm the intended account and workspace, then return to ChatGPT.",
          "Ask one read-only question only that source can answer and require a direct link to the supporting record.",
        ],
        screenshotIds: ["connector-permissions"],
        links: [
          { label: "Follow the paired connector walkthrough", href: "/ai-agent-starter#step-connectors" },
        ],
      },
    ],
    expected: "One connector shows as active and returns current, recognizable information with a working source link.",
    verify: [
      "The connected account and workspace are the intended ones.",
      "The source value and link match what you see in the original tool.",
      "The test stayed read-only and used only the one approved connection.",
    ],
    recover: [
      "The connector is unavailable: use an approved export, file, or redacted screenshot and record the missing connection.",
      "The answer is wrong or stale: compare the account, filters, and source timestamp, then disconnect until the cause is clear.",
    ],
    stop: "Do not approve broad permissions you cannot explain. Stop and ask the data or system owner when the requested scope exceeds the test Job.",
  },
  {
    id: "setup-sea-of-demand",
    kicker: "Install one reusable workflow",
    title: "Add the Sea of Demand Skill to a scoped workspace.",
    instruction: "Use the same reviewed Sea of Demand instructions on either platform. Claude can install the packaged Skill; ChatGPT keeps the instructions inside one Project.",
    paths: [
      {
        platform: "claude",
        label: "Claude path",
        actions: [
          "Download the repository's sea-of-demand.skill file without renaming or unpacking it.",
          "In Claude Desktop, open Skills in Settings or inside the Project that will use it, then choose the option to add a Skill.",
          "Select sea-of-demand.skill, confirm the Skill name and trigger, and keep it attached to one Project when that option is available.",
          "In a new Project chat, ask the Skill to state its deliverables and the inputs it still needs. Do not start live research yet.",
        ],
        screenshotIds: ["skills-library"],
        links: [
          { label: "Download Sea of Demand for Claude", href: "/sea-of-demand.skill", download: true },
          { label: "See the Claude Skill installation screen", href: "/claude-masterclass#module-3" },
        ],
      },
      {
        platform: "chatgpt",
        label: "ChatGPT path",
        actions: [
          "Download sea-of-demand.zip, unzip it, and locate sea-of-demand/SKILL.md.",
          "Create a ChatGPT Project named Sea of Demand and keep its memory scoped to that Project when the option is available.",
          "Open SKILL.md and copy its contents into the Project instructions. Attach the file as a reference too when your approved workspace supports it.",
          "Start a new chat in that Project and ask it to state the workflow's deliverables and the inputs it still needs. Do not start live research yet.",
        ],
        screenshotIds: ["create-project"],
        links: [
          { label: "Download Sea of Demand for ChatGPT", href: "/sea-of-demand.zip", download: true },
          { label: "See how ChatGPT Projects hold reusable work", href: "/ai-agent-starter#step-workflow" },
        ],
        note: "This is a portable-instructions path, not a claim that ChatGPT has Claude's native Skill installer.",
      },
    ],
    expected: "Sea of Demand is either an enabled Claude Skill or a scoped ChatGPT Project whose instructions contain the same SKILL.md workflow.",
    verify: [
      "The workflow is scoped to the intended Project rather than applied to unrelated chats.",
      "A fresh chat can name the expected research deliverables and asks for missing product, customer, and problem context.",
      "No live research, bulk collection, or external action started during the installation check.",
    ],
    recover: [
      "Claude does not accept the file: confirm it still ends in .skill and that Skills are enabled for the current account.",
      "ChatGPT does not accept the file attachment: keep its contents in the Project instructions and save the original file with your other workflow evidence.",
    ],
    stop: "Do not run the Skill against real customer or community data until the research sources, quote use, privacy rules, and reviewer are approved.",
  },
  {
    id: "setup-chief-of-staff",
    kicker: "Finish with one bounded agent",
    title: "Set up the Chief of Staff and run one manual read-only test.",
    instruction: "Create a dedicated workspace, save the full template for later, and prove the smallest safe version before scheduling or expanding it.",
    paths: [
      {
        platform: "claude",
        label: "Claude path",
        actions: [
          "Create a Claude Project named Chief of Staff and add the one connector you verified in the previous step.",
          "Download the full Chief of Staff template and add it to the Project as a reference file. Do not run its recurring-automation instructions during setup.",
          "Copy the setup-only prompt below into a new Project chat. Replace [REVIEWER] and [ONE APPROVED SOURCE].",
          "Run it once, inspect every source link, and save the brief plus your corrections in the Project.",
        ],
        screenshotIds: ["project-library"],
        links: [
          { label: "Download the full Chief of Staff template", href: "/chief-of-staff-agent-prompt.txt", download: true },
          { label: "See the complete Chief of Staff walkthrough", href: "/ai-agent-starter#chief-of-staff-prompt" },
        ],
      },
      {
        platform: "chatgpt",
        label: "ChatGPT path",
        actions: [
          "Create a ChatGPT Project named Chief of Staff and add the one connector you verified in the previous step.",
          "Download the full Chief of Staff template and add it to the Project as a reference file. Do not run its recurring-automation instructions during setup.",
          "Copy the setup-only prompt below into the Project instructions or a new Project chat. Replace [REVIEWER] and [ONE APPROVED SOURCE].",
          "Run it once, inspect every source link, and save the brief plus your corrections in the Project.",
        ],
        screenshotIds: ["create-project"],
        links: [
          { label: "Download the full Chief of Staff template", href: "/chief-of-staff-agent-prompt.txt", download: true },
          { label: "See the complete Chief of Staff walkthrough", href: "/ai-agent-starter#chief-of-staff-prompt" },
        ],
      },
    ],
    expected: "A Project named Chief of Staff contains the full reference template, a setup-only boundary, one approved connection, and one saved read-only brief.",
    verify: [
      "The first run used exactly one approved source and every priority links to its evidence.",
      "The brief separates facts from questions and does not invent an owner, date, or commitment.",
      "No message, record, file, event, purchase, publication, or automation was created or changed.",
      "A named reviewer can accept, correct, or reject the result before the next run.",
    ],
    recover: [
      "The brief is generic: narrow the source, state the review date, and define what qualifies as a priority.",
      "A link or claim cannot be verified: mark it unresolved, remove it from the ranked brief, and correct the source access before rerunning.",
      "The agent tries to act or schedule: stop the run, remove the extra capability, and repeat the setup-only prompt in a clean chat.",
    ],
    stop: "Do not create a recurring automation or grant Change, Send, or Publish authority until repeated manual runs pass review and the accountable owner approves the expansion.",
    prompt: {
      label: "Setup-only Chief of Staff test",
      value: CHIEF_OF_STAFF_SETUP_PROMPT,
    },
  },
];

export const LONGHAND_OPTIONAL_BUILD_SETUP_STEPS: WorkspaceSetupStep[] = [
  {
    id: "optional-github-project",
    kicker: "Optional build setup",
    title: "Give your project a safe GitHub home.",
    instruction: "Use a private practice repository to keep a reviewable history and give Claude Code or Codex one clearly bounded project folder.",
    screenshotIds: ["github-open-with-desktop", "github-clone-location"],
    paths: [
      {
        platform: "claude",
        label: "Claude Code path",
        actions: [
          "Open GitHub's signup page. Enter an organization-approved email address, create your username and password, then follow the prompts to create the account. If your company provides a managed account, use that account instead.",
          "Open GitHub's verification email and verify the address. Then enable two-factor authentication and save the recovery codes somewhere approved and separate from the project folder.",
          "Install GitHub Desktop and sign in through the browser. On GitHub, create a private practice repository with a README. If your organization already assigned a repository, clone that one instead of creating another.",
          "Choose Open with GitHub Desktop from the repository's Code menu. Pick a local folder you will recognize, then select Clone.",
          "Open Claude Code in the Claude Desktop app and select that cloned folder. Terminal users can open a terminal in the folder and run claude.",
          "Ask Claude Code: ‘Read the README and git status. Tell me what this project contains and propose one small change. Do not edit, install, commit, or push anything.’",
        ],
        screenshotIds: ["code-app"],
        links: [
          { label: "Sign up for GitHub", href: "https://github.com/signup" },
          { label: "Follow GitHub's account guide", href: "https://docs.github.com/en/account-and-profile/how-tos/account-management/creating-an-account-on-github" },
          { label: "Create a private repository", href: "https://docs.github.com/en/repositories/creating-and-managing-repositories/creating-a-new-repository" },
          { label: "Install GitHub Desktop", href: "https://docs.github.com/en/desktop/installing-and-authenticating-to-github-desktop/installing-github-desktop" },
          { label: "Authenticate GitHub Desktop", href: "https://docs.github.com/en/desktop/installing-and-authenticating-to-github-desktop/authenticating-to-github-in-github-desktop" },
          { label: "Clone with GitHub Desktop", href: "https://docs.github.com/en/desktop/adding-and-cloning-repositories/cloning-a-repository-from-github-to-github-desktop" },
          { label: "Open a project in Claude Code", href: "https://code.claude.com/docs/en/getting-started" },
        ],
      },
      {
        platform: "chatgpt",
        label: "ChatGPT + Codex path",
        actions: [
          "Open GitHub's signup page. Enter an organization-approved email address, create your username and password, then follow the prompts to create the account. If your company provides a managed account, use that account instead.",
          "Open GitHub's verification email and verify the address. Then enable two-factor authentication and save the recovery codes somewhere approved and separate from the project folder.",
          "Install GitHub Desktop and sign in through the browser. On GitHub, create a private practice repository with a README. If your organization already assigned a repository, clone that one instead of creating another.",
          "Choose Open with GitHub Desktop from the repository's Code menu. Pick a local folder you will recognize, then select Clone.",
          "In the ChatGPT desktop app, choose where to work by opening that cloned folder. Select Codex, then start a New chat in the folder.",
          "Ask Codex: ‘Read the README and git status. Tell me what this project contains and propose one small change. Do not edit, install, commit, or push anything.’",
        ],
        screenshotIds: ["chat-home"],
        links: [
          { label: "Sign up for GitHub", href: "https://github.com/signup" },
          { label: "Follow GitHub's account guide", href: "https://docs.github.com/en/account-and-profile/how-tos/account-management/creating-an-account-on-github" },
          { label: "Create a private repository", href: "https://docs.github.com/en/repositories/creating-and-managing-repositories/creating-a-new-repository" },
          { label: "Install GitHub Desktop", href: "https://docs.github.com/en/desktop/installing-and-authenticating-to-github-desktop/installing-github-desktop" },
          { label: "Authenticate GitHub Desktop", href: "https://docs.github.com/en/desktop/installing-and-authenticating-to-github-desktop/authenticating-to-github-in-github-desktop" },
          { label: "Clone with GitHub Desktop", href: "https://docs.github.com/en/desktop/adding-and-cloning-repositories/cloning-a-repository-from-github-to-github-desktop" },
          { label: "Open a folder in ChatGPT and Codex", href: "https://learn.chatgpt.com/docs/app" },
        ],
      },
    ],
    expected: "GitHub Desktop shows the intended private repository, the project exists in a known local folder, and your coding app can inspect that same folder without changing it.",
    verify: [
      "The GitHub account and repository owner are the ones your organization approved.",
      "The repository is private unless a named owner explicitly approved public access.",
      "The app names the correct folder and git status before proposing work.",
      "No password, API key, .env file, customer data, or other secret was added to the repository or chat.",
    ],
    recover: [
      "The repository will not open in GitHub Desktop: sign into the same GitHub account in the browser and desktop app, then confirm you can access the repository.",
      "The coding app sees the wrong files: close the task and reopen the exact folder shown as Local Path in GitHub Desktop.",
      "Git reports changes before you begin: stop and ask the repository owner whether those changes should be saved, reviewed, or removed.",
    ],
    stop: "Do not make the repository public, add organization members, commit secrets, or let the app edit, commit, or push until the account, folder, and starting git status are correct.",
  },
  {
    id: "optional-vercel-preview",
    kicker: "Optional build setup",
    title: "Connect GitHub to Vercel and review a preview.",
    instruction: "Vercel connects to GitHub, not directly to the coding app. Claude Code or Codex changes the local clone, GitHub records the reviewed branch, and Vercel turns that branch into a preview link.",
    screenshotIds: ["vercel-import-repository"],
    paths: [
      {
        platform: "claude",
        label: "Claude Code path",
        actions: [
          "Open Vercel's signup page and choose Continue with GitHub. Sign into the approved GitHub account, review the requested access, then authorize Vercel.",
          "Complete any email or account verification Vercel requests. Confirm the account name and starting plan, and do not create a paid team or enter billing details unless an accountable owner approved it.",
          "Open the Vercel dashboard. From the correct personal account or organization team, choose Add New, then Project. If your organization already has a Vercel team, use its invitation instead of creating another team.",
          "Grant Vercel access only to the practice repository when that option is available. Find the repository in Import Git Repository and select Import.",
          "Review the project name, framework preset, root directory, build settings, and environment variables. Leave secrets out of the repository; add only required values through the approved Vercel environment controls.",
          "Confirm the repository contains only safe practice content, select Deploy, and open the baseline URL. Do not add a custom domain yet.",
          "Back in Claude Code, ask it to create a branch and make one small, reviewable README change. Inspect the diff before approving a commit or push, then open the Vercel preview created for that branch.",
        ],
        screenshotIds: ["code-safety"],
        links: [
          { label: "Sign up for Vercel", href: "https://vercel.com/signup" },
          { label: "Follow Vercel's account guide", href: "https://vercel.com/docs/accounts" },
          { label: "Deploy a Git repository with Vercel", href: "https://vercel.com/docs/git" },
          { label: "Review Claude Code setup", href: "https://code.claude.com/docs/en/getting-started" },
        ],
      },
      {
        platform: "chatgpt",
        label: "ChatGPT + Codex path",
        actions: [
          "Open Vercel's signup page and choose Continue with GitHub. Sign into the approved GitHub account, review the requested access, then authorize Vercel.",
          "Complete any email or account verification Vercel requests. Confirm the account name and starting plan, and do not create a paid team or enter billing details unless an accountable owner approved it.",
          "Open the Vercel dashboard. From the correct personal account or organization team, choose Add New, then Project. If your organization already has a Vercel team, use its invitation instead of creating another team.",
          "Grant Vercel access only to the practice repository when that option is available. Find the repository in Import Git Repository and select Import.",
          "Review the project name, framework preset, root directory, build settings, and environment variables. Leave secrets out of the repository; add only required values through the approved Vercel environment controls.",
          "Confirm the repository contains only safe practice content, select Deploy, and open the baseline URL. Do not add a custom domain yet.",
          "Back in Codex, ask it to create a branch and make one small, reviewable README change. Inspect the diff before approving a commit or push, then open the Vercel preview created for that branch.",
        ],
        screenshotIds: [],
        links: [
          { label: "Sign up for Vercel", href: "https://vercel.com/signup" },
          { label: "Follow Vercel's account guide", href: "https://vercel.com/docs/accounts" },
          { label: "Deploy a Git repository with Vercel", href: "https://vercel.com/docs/git" },
          { label: "Review the ChatGPT desktop app", href: "https://learn.chatgpt.com/docs/app" },
        ],
      },
    ],
    expected: "The approved GitHub repository is connected to the correct Vercel account or team, and a reviewed working branch creates a separate preview URL before later changes are merged to the production branch.",
    verify: [
      "The Vercel project points to the intended GitHub owner, repository, production branch, and root directory.",
      "The preview URL shows only the reviewed change from the test branch.",
      "The coding app's diff, the GitHub branch, and the Vercel preview describe the same change.",
      "No secret appears in git history, the browser address, build output, or the previewed page.",
    ],
    recover: [
      "The repository is missing in Vercel: check the selected GitHub account and update the Vercel GitHub integration to grant access to that repository only.",
      "The deployment fails: open the Vercel build log, copy the first actionable error into the same coding-app task, and ask for a diagnosis before allowing another change.",
      "The preview does not match the branch: confirm the commit, branch name, root directory, and deployment source before trying again.",
    ],
    stop: "Do not merge to the production branch, attach a production domain, expose a secret, or deploy inside an employer's Vercel team without the repository owner and accountable reviewer approving it.",
  },
];
